import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  declaredValueValidator,
  deleteSymbols,
  recipientCashOnDeliveryValidator,
  s10CodeValidator,
  shelfLifeRangeValidator,
} from '../../utils';
import { AddressWidgetPickerConfigType } from '$shared/components/address-widget-picker';
import { TranslateService } from '@ngx-translate/core';
import { ADDONS, OBTAINING_METHODS, TYPES_OF_DELIVERY } from '../../metadata';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import {
  Batch,
  BatchMailingData,
  BatchMailingType,
  BatchProperties,
  ListOption,
} from '../../models';
import { phoneValidator } from '$shared/validators/phoneValidator';
import { removePhonePrefix } from '$shared/utils';
import { User } from '$core/models';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { SessionService } from '$core/services';
import { BatchesParseStorageService } from '../../services';

@Component({
  selector: 'app-batch-mailing-form',
  templateUrl: './batch-mailing-form.component.html',
  styleUrls: ['./batch-mailing-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchMailingFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly sessionService = inject(SessionService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly batchesParseStorageService = inject(
    BatchesParseStorageService
  );

  @Input() public batch: Batch | null = null;

  @Input() public batchProperties: BatchProperties | null = null;

  @Input() public batchMailingData: BatchMailingData[BatchMailingType] | null =
    null;

  public readonly form$ = new BehaviorSubject<FormGroup | null>(null);

  public readonly addonsControl = new FormControl([]);

  public readonly deleteSymbols = deleteSymbols;

  protected emsTimePeriod =
    this.batchesParseStorageService.generateEMSTimePeriod();

  protected TYPES_OF_DELIVERY = TYPES_OF_DELIVERY;
  protected readonly OBTAINING_METHODS = OBTAINING_METHODS;

  protected readonly addons$ = new BehaviorSubject<readonly ListOption[]>([]);
  protected readonly notifications$ = new BehaviorSubject<
    readonly ListOption[]
  >([]);

  protected readonly recipientWidgetConfig =
    computed<AddressWidgetPickerConfigType>(() => ({
      context: 'batch-mailing',
      title: this.translateService.instant(
        'BATCH_MAILING.BATCHES_TAB.CREATE_MODAL.RECIPIENT_NAME'
      ),
      addressPersonType: 'recipient',
      belpostServiceType: 'batch_mailing',
      countryCode: 'BY',
      isCashOnDelivery: this.isCashOnDelivery(),
    }));

  private readonly user$ = this.sessionService.session$;

  /**
   * Whether the cash on delivery control is filled.
   */
  private readonly isCashOnDelivery = signal(false);

  public ngOnInit(): void {
    this.setupForm();
  }

  public ngOnDestroy(): void {}

  public setObtainingMethod(): void {
    if (this.form.get('phone').value) {
      this.form.get('obtainingMethod').patchValue('0');
      this.form.get('email').patchValue(null);
      this.form.get('email').clearValidators();
      this.form.get('email').updateValueAndValidity();
    }

    if (this.form.get('email').value) {
      this.form.get('obtainingMethod').patchValue('1');
      this.form.get('phone').patchValue(null);
      this.form.get('phone').clearValidators();
      this.form.get('phone').updateValueAndValidity();
    }
  }

  public handleTimeOfDelivery(): void {
    if (this.batchProperties.isEMS) {
      const recipient = this.form.get('recipient').value;

      if (recipient) {
        this.changeTimeOfDelivery(
          // recipient.address.is_district_center,
          false,
          recipient.address.city
        );
      }
    }
  }

  public markForCheck(): void {
    this.cd.markForCheck();
  }

  public setupForm(): void {
    this.initForm();
    this.handleBatchData();
    this.observeForm();
  }

  public get form(): FormGroup | null {
    return this.form$.value;
  }

  protected getControlValidity(controlNameArr: readonly string[]): boolean {
    return controlNameArr.some((controlName) => {
      const control = this.form.get(controlName);

      if (control) {
        control.markAsTouched();
      }

      return control && control.invalid && control.errors;
    });
  }

  protected setControlError(
    controlNamesArr: readonly string[]
  ): ValidationErrors {
    let controlErrors: ValidationErrors = {};

    controlNamesArr.forEach((controlName) => {
      const control = this.form.get(controlName);

      if (control && control.errors) {
        controlErrors = this.mergeErrors(controlErrors, control.errors);
      }
    });

    return controlErrors;
  }

  protected obtainingMethodChange(): void {
    const phoneControl = this.form.get('phone');
    const emailControl = this.form.get('email');

    if (this.form.get('notification').value === '5') {
      if (this.form.get('obtainingMethod').value === '0') {
        if (phoneControl) {
          phoneControl.patchValue(
            this.user.phone ? removePhonePrefix(this.user.phone) : null
          );
          phoneControl.setValidators([
            Validators.required,
            phoneValidator(),
            Validators.minLength(9),
          ]);
        }

        if (emailControl) {
          emailControl.patchValue(null);
          emailControl.clearValidators();
        }
      }

      if (this.form.get('obtainingMethod')?.value === '1') {
        if (emailControl) {
          emailControl.patchValue(this.user?.email ? this.user.email : null);
          emailControl.setValidators([Validators.required, Validators.email]);
        }

        if (this.batchProperties && this.batchProperties.isEcommerce) {
          /* Временная заплатка пока не починят ПАК, потом удалить */
          if (emailControl) emailControl.disable();
        }

        if (phoneControl) {
          phoneControl.patchValue(null);
          phoneControl.clearValidators();
        }
      }
    } else {
      if (phoneControl) {
        phoneControl.patchValue(null);
        phoneControl.clearValidators();
      }

      if (emailControl) {
        emailControl.patchValue(null);
        emailControl.clearValidators();
      }
    }

    if (emailControl) emailControl.updateValueAndValidity();
    if (phoneControl) phoneControl.updateValueAndValidity();
  }

  protected onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;
    const validCodes = ['25', '29', '33', '44'];

    const cleanedInput = inputValue.replace(/\D/g, '');
    const code = cleanedInput.slice(3, 5);

    if (
      code.length === 2 &&
      !validCodes.includes(code) &&
      inputValue.length === 11
    ) {
      inputElement.value = inputElement.value.slice(0, -3);
    }
  }

  protected get formGroupTimeOfDelivery() {
    return this.form.get('time_of_delivery') as FormGroup;
  }

  protected get formGroupTimeInterval() {
    return this.form.get('time_of_delivery').get('time_interval') as FormGroup;
  }

  private initForm(): void {
    if (!this.batchMailingData) {
      return;
    }

    const formGroup = this.createFormGroup();

    this.form$.next(formGroup);
    this.addonsControl.setValue([]);

    if (this.batchProperties && this.batchProperties.isEcommerce) {
      this.form.get('obtainingMethod').patchValue('1');
      this.form.get('obtainingMethod').disable();
      this.obtainingMethodChange();
    }

    if (
      this.batchProperties &&
      !this.batchProperties.isELS &&
      this.batchProperties.isEcommerce
    ) {
      this.form.get('shelf_life').patchValue(null);
      this.form.get('shelf_life').clearValidators();
    }
  }

  private createFormGroup(): FormGroup {
    const formFields = { ...this.batchMailingData.form, checked: true };

    const formControls = Object.keys(formFields).reduce((controls, key) => {
      if (formFields[key].formGroup) {
        const formGroup = formFields[key].formGroup;

        const groupControls = formGroup.reduce((acc, control) => {
          if (control.formGroup) {
            const innerGroupControls = Object.keys(control.formGroup).reduce(
              (innerAcc, innerKey) => {
                innerAcc[innerKey] = this.fb.control(
                  control.formGroup[innerKey].value,
                  control.formGroup[innerKey].validation
                );
                return innerAcc;
              },
              {}
            );
            acc[control.name] = this.fb.group(innerGroupControls);
          } else {
            acc[control.name] = this.fb.control(
              {
                value: control.value || '',
                disabled: control.disabled || false,
              },
              control.validation || []
            );
          }
          return acc;
        }, {});

        controls[key] = this.fb.group(groupControls);
      } else {
        let validators = formFields[key].validation || [];

        if (key === 'shelf_life') {
          validators = validators.slice([shelfLifeRangeValidator(10, 30)]);
        }

        controls[key] = this.fb.control(
          {
            value: !formFields[key].disabled ? formFields[key].value : '',
            disabled: formFields[key].disabled,
          },
          validators
        );
      }

      return controls;
    }, {});

    return this.fb.group(formControls, {
      validators: [
        declaredValueValidator,
        recipientCashOnDeliveryValidator,
        s10CodeValidator,
      ],
    });
  }

  private observeForm(): void {
    if (!this.form) {
      return;
    }

    this.listenToDeliveryTypeChange();
    this.listenToNotificationChange();
    this.listenToRecipient();
    this.listenToAddonsControl();
    this.listenToCashOnDelivery();

    if (this.formGroupTimeOfDelivery) {
      this.handleTimeIntervalFormGroup();
    }
  }

  private mergeErrors(
    errors: ValidationErrors,
    newErrors: ValidationErrors
  ): ValidationErrors {
    const mergedErrors: ValidationErrors = { ...errors };

    for (const key in newErrors) {
      if (newErrors.hasOwnProperty(key)) {
        const errorKey = key.toString();

        if (mergedErrors[errorKey]) {
          mergedErrors[errorKey] += ', ' + newErrors[key];
        } else {
          mergedErrors[errorKey] = newErrors[key];
        }
      }
    }

    return mergedErrors;
  }

  private listenToDeliveryTypeChange(): void {
    this.form.valueChanges
      .pipe(startWith(this.form.value), untilDestroyed(this))
      .subscribe((formRawData) => {
        if (this.batchProperties.isOrderedLetterOrPostcard) {
          if (formRawData.recipient) {
            switch (formRawData.recipient.person?.type) {
              case 'legal':
              case 'individual_entrepreneur': {
                const newAddons = this.addons.filter(
                  (addon) => addon.value !== 'hand_over_personally'
                );

                if (
                  this.form.get('hand_over_personally').value === true /*  &&
                  this.form.get('hand_over_personally').invalid */
                ) {
                  this.form
                    .get('hand_over_personally')
                    .patchValue(false, { emitEvent: false });
                }

                if (JSON.stringify(this.addons) !== JSON.stringify(newAddons)) {
                  this.addons$.next(newAddons);

                  this.addonsControl.patchValue([], { emitEvent: false });

                  this.notifications$.next(
                    this.batchMailingData.notifications.slice()
                  );
                }

                break;
              }

              case 'individual':
                const newAddons = ADDONS.filter((el) =>
                  Object.keys(this.batchMailingData.form).includes(el.value)
                );

                this.addons$.next(newAddons);

                if (formRawData.hand_over_personally) {
                  this.form
                    .get('notification')
                    .patchValue('2', { emitEvent: false });

                  this.notifications$.next([
                    {
                      label:
                        'BATCH_MAILING.BATCHES_TAB.METADATA.NOTIFICATION_ORDERED',
                      value: '2',
                    },
                  ]);
                } else {
                  this.notifications$.next(
                    this.batchMailingData.notifications.slice()
                  );
                }

                break;
            }
          } else {
            this.notifications$.next(
              this.batchMailingData.notifications.slice()
            );
          }
        }

        if (this.batch.postal_delivery_type === 'ordered_letter') {
          if (formRawData.subpoena || formRawData.hand_over_personally) {
            this.form.get('notification').patchValue('2', { emitEvent: false }); // только заказное
            this.notifications$.next([
              {
                label:
                  'BATCH_MAILING.BATCHES_TAB.METADATA.NOTIFICATION_ORDERED',
                value: '2',
              },
            ]);
          } else {
            this.notifications$.next(
              this.batchMailingData.notifications.slice()
            );
          }
        }
      });
  }

  private listenToNotificationChange(): void {
    this.form
      .get('notification')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.obtainingMethodChange();
      });
  }

  private listenToCashOnDelivery(): void {
    this.form
      .get('cash_on_delivery')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value: boolean) => {
        this.isCashOnDelivery.set(Boolean(value));
      });
  }
  private listenToRecipient(): void {
    this.form
      .get('recipient')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((formRawData) => {
        if (formRawData) {
          if (this.batchProperties.isEMS) {
            this.emsTimePeriod =
              this.batchesParseStorageService.generateEMSTimePeriod(
                formRawData.is_district_center,
                formRawData.address.city
              );

            const fromValue =
              this.formGroupTimeInterval.get('from') &&
              this.formGroupTimeInterval.get('from').value;
            const toValue =
              this.formGroupTimeInterval.get('to') &&
              this.formGroupTimeInterval.get('to').value;

            if (fromValue && !this.emsTimePeriod.includes(fromValue)) {
              // обнуляем значение периода "от" если его нет в массиве времен работы ОП
              this.formGroupTimeInterval
                .get('from')
                .patchValue(null, { emitEvent: false });
            }
            if (toValue && !this.emsTimePeriod.includes(toValue)) {
              // обнуляем значение периода "до" если его нет в массиве времен работы ОП
              this.formGroupTimeInterval
                .get('to')
                .patchValue(null, { emitEvent: false });
            }
          }
        }
      });
  }

  private changeTimeOfDelivery(
    is_district_center: boolean,
    city: string
  ): void {
    if (
      !is_district_center &&
      city.toLowerCase() !== 'новополоцк' &&
      city.toLowerCase() !== 'жодино'
    ) {
      this.TYPES_OF_DELIVERY = [
        {
          label: 'DELIVERY.LEVEL1',
          value: 'level1' as 'level1',
        },
        {
          label: 'DELIVERY.LEVEL2',
          value: 'level2' as 'level2',
        },
      ];

      this.form.get('time_of_delivery').patchValue({
        type: 'level1',
        time_interval: '',
      });
    } else {
      this.TYPES_OF_DELIVERY = TYPES_OF_DELIVERY;
    }
  }

  private listenToAddonsControl(): void {
    this.addonsControl.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((addons: string[]) => {
        this.addons.forEach((el) => {
          if (el.value !== 'time_of_delivery') {
            const control = this.form.get(el.value);

            if (control) {
              control.setValue(addons.includes(el.value));
            }
          }
        });

        this.addonsControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  private handleTimeIntervalFormGroup(): void {
    this.form
      .get('time_of_delivery')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.form.get('time_of_delivery').value.type !== 'level4') {
          this.formGroupTimeInterval
            .get('from')
            .patchValue('', { emitEvent: false });
          this.formGroupTimeInterval
            .get('to')
            .patchValue('', { emitEvent: false });
          this.formGroupTimeInterval.get('to').clearValidators();
          this.formGroupTimeInterval
            .get('to')
            .updateValueAndValidity({ emitEvent: false });
          this.formGroupTimeInterval.get('from').clearValidators();
          this.formGroupTimeInterval
            .get('from')
            .updateValueAndValidity({ emitEvent: false });
        }

        if (this.form.get('time_of_delivery').value.type === 'level4') {
          this.formGroupTimeInterval
            .get('to')
            .setValidators(Validators.required);
          this.formGroupTimeInterval
            .get('from')
            .setValidators(Validators.required);
        }
      });
  }

  private handleFormFieldsDisable(): void {
    if (!this.batch.is_declared_value) {
      this.form.get('declared_value').disable();
    }

    switch (this.batch.negotiated_rate) {
      case '0': {
        if (!this.batch.is_declared_value) {
          this.form.get('cash_on_delivery').disable();
        }

        break;
      }

      case '1':
        this.form.get('declared_value').disable();
        this.form.get('cash_on_delivery').enable();

        break;
    }
  }

  private handleBatchData(): void {
    if (this.batchProperties.isEMS) {
      if (!!this.batch.is_document) {
        this.form.controls.weight.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(2000),
        ]);
      } else {
        this.form.controls.weight.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(50000),
        ]);
      }
    }

    if (this.batch.is_declared_value) {
      this.form.controls.declared_value.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(999999999),
      ]);
    }

    this.handleFormFieldsDisable();

    this.notifications$.next(this.batchMailingData.notifications.slice());

    this.addons$.next(
      ADDONS.filter(
        (el) =>
          this.batchMailingData.addons &&
          Object.keys(this.batchMailingData.addons).includes(el.value) &&
          !el.value.includes('time_of_delivery')
      )
    );
  }

  private updateSpecialMarksValidity(): void {
    Object.keys(this.form.controls).forEach((controlName) => {
      const control = this.form.get(controlName);
      if (control && control.invalid) {
        control.updateValueAndValidity();
      }
    });
  }

  private get user(): User | null {
    return this.user$.value;
  }

  private get addons(): readonly ListOption[] {
    return this.addons$.value;
  }
}
